import { Array as A, pipe } from 'effect';
import type { PDFDocumentProxy } from 'pdfjs-dist';

/** The page box as stored in the PDF file, in its own units, not swapped by any rotation. */
export type PdfPageBox = { width: number; height: number };

// A page as pdf.js draws it at 100% with the rotation stored in the file applied, that rotation, and the page box.
export type PageGeometry = { width: number; height: number; fileRotation: number; pdfPageBox: PdfPageBox };

export const BORDER_WIDTH = 30;
export const MAX_DOC_WIDTH = 870;

/** The width react-pdf draws a page at, before it applies the zoom, in a pane of this width. */
export const pageWidthOfPane = (paneWidth: number) => Math.min(MAX_DOC_WIDTH, paneWidth) - BORDER_WIDTH;

/** The pages read their zoom from this property while the divider is dragged. */
export const PAGES_ZOOM_WHILE_RESIZING = '--pages-zoom-while-resizing';

/** Shows the pages drawn for one pane width at the size they would have in another. */
export const pageZoomWhileResizing = (drawnPaneWidth: number, paneWidth: number) =>
  pageWidthOfPane(paneWidth) / pageWidthOfPane(drawnPaneWidth);

// A page whose size cannot be read gets the A4 size; react-pdf shows that page's own load error.
const A4_PAGE: PageGeometry = { width: 595, height: 842, fileRotation: 0, pdfPageBox: { width: 595, height: 842 } };

// The viewport, not the raw page box: the box ignores the file's rotation and may be offset or negative.
const readPageGeometry = async (pdf: PDFDocumentProxy, pageNumber: number): Promise<PageGeometry> => {
  const page = await pdf.getPage(pageNumber);
  const { width, height } = page.getViewport({ scale: 1 });
  const [left, bottom, right, top] = page.view;
  return {
    width,
    height,
    fileRotation: page.rotate,
    pdfPageBox: { width: Math.abs(right - left), height: Math.abs(top - bottom) },
  };
};

export const readPageGeometries = async (pdf: PDFDocumentProxy): Promise<PageGeometry[]> =>
  pipe(
    await Promise.allSettled(A.makeBy(pdf.numPages, (i) => readPageGeometry(pdf, i + 1))),
    A.map((result) => (result.status === 'fulfilled' ? result.value : A4_PAGE)),
  );

const isSideways = (rotation: number) => (rotation / 90) % 2 === 1;

// react-pdf's `rotate` replaces the file's rotation instead of adding to it, so it gets the sum.
export const displayedRotation = (fileRotation: number, userRotation: number) => (fileRotation + userRotation) % 360;

export const pageHeightAtWidth = (page: PageGeometry, userRotation: number, width: number) =>
  isSideways(userRotation) ? (page.width * width) / page.height : (page.height * width) / page.width;

export const pagesAtWidth = (
  pages: ReadonlyArray<PageGeometry>,
  userRotationOfPage: (page: number) => number,
  width: number,
  scale: number,
) =>
  A.map(pages, (page, idx) => {
    const userRotation = userRotationOfPage(idx);
    return {
      userRotation,
      displayedRotation: displayedRotation(page.fileRotation, userRotation),
      zoomedHeight: pageHeightAtWidth(page, userRotation, width) * scale,
      pdfPageBox: page.pdfPageBox,
    };
  });

export const pageThumbnailsAtWidth = (
  pages: ReadonlyArray<PageGeometry>,
  userRotationOfPage: (page: number) => number,
  width: number,
) =>
  A.map(pages, (page, idx) => {
    const userRotation = userRotationOfPage(idx);
    return {
      displayedRotation: displayedRotation(page.fileRotation, userRotation),
      height: pageHeightAtWidth(page, userRotation, width),
    };
  });
